'use client';

import type {
  InventoryResource,
  Snapshot,
  SourceAccount,
} from '@repo/api-gw-sdk';
import { AccountState, Provider } from '@repo/api-gw-sdk';

import { Loader } from '@/components/layout/loading';
import { useDAL } from '@/contexts/useDAL';
import { useSourceAccounts } from '@/contexts/useSourceAccounts';

import { DocumentdbConfiguration } from './steps/documentdbConfiguration';

import type { Encryption } from '../encryption/encryptionComponent';
import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface RestoreDocumentdbState {
  resourceId: string;
  snapshot: Snapshot;
  clusterIdentifier?: string;
  parameterGroupName?: string;
  subnetGroupName?: string;
  vpcSecurityGroupIds: string[];
  port?: number;
  supportedCloudProviders: Provider[];
  restoreAccount?: SourceAccount;
  accounts?: SourceAccount[];
  emptyMessage?: string;
  regionName?: string;
  initialTags: Record<string, string>;
  tags: Record<string, string>;
  keepOriginalTags: boolean;
  encryption?: Encryption;
  initialRegionName: string;
}

const restoreDocumentdbFlow: Step<RestoreDocumentdbState> = {
  id: 'documentdb_configuration',
  Component: DocumentdbConfiguration,
};

export const RestoreDocumentdbWizard = ({
  resource,
  snapshot,
  onClose,
  onBack,
}: {
  resource: InventoryResource;
  snapshot: Snapshot;
  onClose: () => void;
  onBack: () => void;
}) => {
  const { sourceAccounts } = useSourceAccounts();
  const dal = useDAL();
  const { data: restoreAccountsResponse, isLoading } =
    dal.cloudAccounts.restore.useList();

  const sourceAccount = sourceAccounts?.find(
    (account) =>
      account.providerAccountId === resource.providerAccountId &&
      account.sourceAccountAttributes.cloudProvider === Provider.Aws &&
      account.status === AccountState.ACCOUNT_STATE_CONNECTED
  );

  const hasRestoreAccount = restoreAccountsResponse?.accounts?.some(
    (account) =>
      account.providerAccountId === resource.providerAccountId &&
      account.status === AccountState.ACCOUNT_STATE_CONNECTED
  );

  const allowedAccounts =
    sourceAccount && hasRestoreAccount ? [sourceAccount] : [];

  if (isLoading || !sourceAccounts || !restoreAccountsResponse) {
    return <Loader />;
  }

  return (
    <Wizard<RestoreDocumentdbState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreDocumentdbFlow]}
      initialState={{
        initialRegionName:
          snapshot.documentDbSnapshotProperties?.region ||
          resource.region ||
          'us-east-1',
        snapshot,
        resourceId: snapshot.resourceId,
        supportedCloudProviders: [Provider.Aws],
        restoreAccount: allowedAccounts.length > 0 ? sourceAccount : undefined,
        accounts: allowedAccounts,
        keepOriginalTags: true,
        initialTags: snapshot.resource?.tags || {},
        tags: snapshot.resource?.tags || {},
        vpcSecurityGroupIds: [],
        port: 27017,
        emptyMessage: !sourceAccount
          ? 'The source account for this DocumentDB resource is not connected. DocumentDB backups are stored in AWS Backup Vault in the source account and can only be restored to the same account.'
          : !hasRestoreAccount
            ? `A restore account is required for the same AWS account (${resource.providerAccountId}). Please onboard a restore account for this AWS account.`
            : undefined,
      }}
      title='Restore DocumentDB'
      stepperLabels={[]}
    />
  );
};
